import React, { useState, useEffect } from 'react';
import { Phone, MapPin, Mail, User, Building2, Clock, Globe, Star, Loader2, AlertCircle } from 'lucide-react';

const ContactPage = () => {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDealers = async () => {
      try {
        setLoading(true);
        const apiUrl = process.env.NODE_ENV === 'development' 
          ? 'http://localhost:5001/api/dealers'
          : '/api/dealers';
        
        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch dealers');
        }
        
        const data = await response.json();
        
        // Transform API data to match UI structure
        const transformedDealers = data.dealers.map(d => ({
          id: d.id,
          name: d.dealerName,
          manager: d.contactName || null,
          phone: d.phone,
          email: d.email,
          address: `${d.address}, ${d.city}, ${d.state} ${d.zipCode}`,
          hours: d.hours,
          website: d.website ? d.website.replace(/^https?:\/\//, '') : '',
          featured: d.dealerType === 'Premier',
          specialNote: d.notes || null,
          lat: d.latitude,
          lng: d.longitude
        }));
        
        setDealers(transformedDealers);
        setError(null);
      } catch (err) {
        console.error('Error fetching dealers:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDealers();
  }, []);

  if (loading) {
    return (
      <div className="contact-page">
        <div className="page-header">
          <h1>Authorized Centurion Dealers</h1>
          <p>Connect with our trusted network of dealers across the country</p>
        </div>
        <div className="loading-container" style={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '4rem',
          gap: '1rem'
        }}>
          <Loader2 size={48} className="animate-spin" style={{ color: '#8C1515' }} />
          <p style={{ color: '#666' }}>Loading dealers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="contact-page">
        <div className="page-header">
          <h1>Authorized Centurion Dealers</h1>
          <p>Connect with our trusted network of dealers across the country</p>
        </div>
        <div className="error-container" style={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '4rem',
          gap: '1rem',
          background: '#FEF2F2',
          borderRadius: '12px',
          margin: '2rem'
        }}>
          <AlertCircle size={48} style={{ color: '#DC2626' }} />
          <p style={{ color: '#DC2626', fontWeight: '600' }}>Unable to load dealers</p>
          <p style={{ color: '#666' }}>{error}</p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#8C1515',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <div className="page-header">
        <h1>Authorized Centurion Dealers</h1>
        <p>Connect with our trusted network of dealers across the country</p>
      </div>

      <div className="dealers-grid">
        {dealers.map(dealer => (
          <div 
            key={dealer.id} 
            className={`dealer-card ${dealer.featured ? 'featured' : ''}`}
          >
            {dealer.featured && (
              <div className="featured-badge">⭐ Premier Dealer</div>
            )}
            
            <div className="dealer-header">
              <Building2 size={24} className="dealer-icon" />
              <h3>{dealer.name}</h3>
            </div>

            <div className="dealer-info">
              {dealer.manager && (
                <div className="info-row">
                  <User size={16} />
                  <span><strong>Contact:</strong> {dealer.manager}</span>
                </div>
              )}
              
              <div className="info-row">
                <Phone size={16} />
                <a href={`tel:${dealer.phone.replace(/\D/g, '')}`}>
                  {dealer.phone}
                </a>
              </div>
              
              <div className="info-row">
                <Mail size={16} />
                <a href={`mailto:${dealer.email}`}>
                  {dealer.email}
                </a>
              </div>
              
              <div className="info-row">
                <MapPin size={16} />
                <span>{dealer.address}</span>
              </div>
              
              <div className="info-row">
                <Clock size={16} />
                <span>{dealer.hours}</span>
              </div>
              
              {dealer.website && (
                <div className="info-row">
                  <Globe size={16} />
                  <a href={`https://${dealer.website}`} target="_blank" rel="noopener noreferrer">
                    {dealer.website}
                  </a>
                </div>
              )}
            </div>

            {dealer.specialNote && (
              <div className="special-note">
                <Star size={14} />
                <span>{dealer.specialNote}</span>
              </div>
            )}

            <div className="dealer-actions">
              <a href={`tel:${dealer.phone.replace(/\D/g, '')}`} className="contact-button">
                <Phone size={14} />
                Call Now
              </a>
              <a href={`mailto:${dealer.email}`} className="contact-button secondary">
                <Mail size={14} />
                Email
              </a>
            </div>
          </div>
        ))}
      </div>

      <div className="contact-footer">
        <div className="corporate-info">
          <h3>Centurion Boats Corporate</h3>
          <p>For general inquiries and corporate matters:</p>
          <div className="corporate-details">
            <div className="info-row">
              <Phone size={16} />
              <span>1-888-CENTURION</span>
            </div>
            <div className="info-row">
              <Mail size={16} />
              <span>info@centurionboats.com</span>
            </div>
            <div className="info-row">
              <MapPin size={16} />
              <span>Centurion Boats, Merced, CA 95340</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
